import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast, { Toaster } from 'react-hot-toast';

import clsx from 'clsx';

import {
  reducerDeletedExpiredReports,
  reducerExpiredReportsInputValues,
  reducerExpiredReportsStatus,
} from '../../../redux/reducer/userReports/reducers/expiredReports';
import { actionGetExpiredReports } from '../../../redux/action/userReports/actionExpiredReports';
import { actionGetUserReportsFilter } from '../../../redux/action/userReports/actionUserReports';

import { generateStyles } from '../../../utils/generateToastStyles';
import { formatReports } from '../../../utils/formatReports';
import {
  shouldShowSupervisorsColumn,
  shouldShowSupervisorsInReports,
} from '../../../utils/reportSupervisors';
import { AppDispatch } from '../../../redux/reducer/store';

import CustomMultiSelect from '../../custom/customMultiSelect/customMultiSelect';
import RangeDatePicker from '../../custom/rangeDatePicker/rangeDatePicker';
import LimitSelect from '../../custom/limitSelect/limitSelect';
import ToastPopup from '../../custom/toastPopup/toastPopup';
import Pagination from '../../pagination/pagination';
import Select from '../../custom/select/select';
import Loader from '../../custom/loader/loader';

import '../table.scss';
import { IUserOptions } from '../../../types/interfaces';
import { IExpiredReportOptions } from '../../../types/reportsInterfaces';

export const employeeStatusesOptions = [
  { value: '', label: 'Любой' },
  { value: 'Active', label: 'Активный' },
  { value: 'OutSick', label: 'Больничный' },
  { value: 'Vacant', label: 'Вакант' },
  { value: 'LingeringVacant', label: 'Долгий вакант' },
  { value: 'Replacement', label: 'Замена' },
  { value: 'Unprofitable', label: 'Нерентабельный' },
  { value: 'DayOff', label: 'Отгул' },
  { value: 'Vacation', label: 'Отпуск' },
];

const IS_VISIBLE_SIDEBAR = process.env.REACT_APP_IS_VISIBLE_SIDEBAR;
const userData = JSON.parse(localStorage.getItem('userData') as string);

const requestStatusOptions = [
  {
    status: 204,
    purpose: 'create',
    successMessage: 'Отчеты успешно созданы!',
  },
  {
    status: 0,
    purpose: 'create',
    errorMessage: 'При создании отчетов произошла ошибка!',
  },
  {
    status: 'noData',
    purpose: 'create',
    errorMessage: 'Выделите хотя бы одну запись в таблице!',
  },
  {
    status: 204,
    purpose: 'delete',
    successMessage: 'Выбранные отчеты успешно удалены!',
  },
  {
    status: 0,
    purpose: 'delete',
    errorMessage: 'При удалении отчетов произошла ошибка!',
  },
  {
    status: 'noData',
    purpose: 'delete',
    errorMessage: 'Выделите хотя бы одну запись в таблице!',
  },
  {
    status: 204,
    purpose: 'export',
    successMessage: `Отчет будет отправлен на почту ${userData?.email}, когда будет сформирован!`,
  },
  {
    status: 0,
    purpose: 'export',
    errorMessage: 'Ошибка загрузки файлов!',
  },
];

const emptyMerchOption = [
  {
    value: null,
    label: ' Без мерчендайзера',
  },
];

const isExpiredReportMerchPresent = (report: IExpiredReportOptions): boolean =>
  Boolean(report.merch?.login && String(report.merch.login).trim());

const getExpiredReportRowClassName = (
  report: IExpiredReportOptions,
): 'red__row' | 'white__row' =>
  isExpiredReportMerchPresent(report) ? 'white__row' : 'red__row';

const TableExpiredReports: FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    expiredReportsInputValues,
    expiredReportsSettings,
    expiredReportsCount,
    formattedDateFrom,
    formattedDateTo,
    requestPurpose,
    checkboxes,
    reports,
    status,
    limit,
    page,
  } = useSelector((state: any) => state.userReports.expiredReports);
  const {
    contractsOptions,
    projectOptions,
    chainsOptions,
    merchsOptions,
    svsOptions,
    vsvsOptions,
  } = useSelector((state: any) => state.userReports.userReports);
  const { data, isOpenSidebar } = useSelector(
    (state: IUserOptions) => state.user.user,
  );
  const { claims } = useSelector((state: IUserOptions) => state.user.user.data);
  const showSupervisorsColumn = useMemo(
    () =>
      shouldShowSupervisorsInReports(data?.settings) &&
      shouldShowSupervisorsColumn(reports),
    [data?.settings, reports],
  );

  const [isHeaderCheckboxChecked, setIsHeaderCheckboxChecked] =
    useState<boolean>(false);
  const [isCheckedRows, setIsCheckedRows] = useState<boolean[]>(checkboxes);
  const [isPending, setIsPending] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [startPage, setStartPage] = useState<number>(1);
  const [merchsOptionsWithEmpty, setMerchsOptionsWithEmpty] = useState([
    ...emptyMerchOption,
  ]);

  const actualLimit =
    JSON.parse(localStorage.getItem('pageLimit') as string) || limit;

  const checkboxList = (check: boolean) => {
    const updatedCheckedRows =
      reports &&
      reports.reduce(
        (acc: { [key: number]: boolean }, report: IExpiredReportOptions) => {
          acc[report.expiredReport.id] = check;
          return acc;
        },
        {},
      );
    dispatch(reducerDeletedExpiredReports(formatReports(updatedCheckedRows)));
    setIsCheckedRows(updatedCheckedRows);
  };

  useEffect(() => {
    checkboxList(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports]);

  useEffect(() => {
    setIsPending(true);
    dispatch(actionGetUserReportsFilter());
    dispatch(actionGetExpiredReports(actualLimit, page)).then(() => {
      setIsPending(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (merchsOptions) {
      setMerchsOptionsWithEmpty([...emptyMerchOption, ...merchsOptions]);
    }
  }, [merchsOptions]);
  useEffect(() => {
    if (status === 204 && requestPurpose === 'create') {
      setIsCheckedRows(checkboxes);
    }
    requestStatusOptions.forEach((option) => {
      if (status === option.status && requestPurpose === option.purpose) {
        toast.dismiss();
        toast(
          (t) => (
            <ToastPopup
              title={
                option.successMessage
                  ? option.successMessage
                  : option.errorMessage
              }
              isSuccess={option.successMessage ? true : false}
              toast={toast}
              t={t}
            />
          ),
          generateStyles(option.successMessage ? true : false),
        );
      }
    });

    setTimeout(() => {
      dispatch(reducerExpiredReportsStatus(null));
    }, 5000);
  }, [data, dispatch, requestPurpose, status, checkboxes]);

  const resetPages = () => {
    setCurrentPage(1);
    setStartPage(1);
  };

  const handleHeaderCheckboxChange = () => {
    if (claims.includes('admin') || claims.includes('reports:expired:delete')) {
      checkboxList(!isHeaderCheckboxChecked);
      setIsHeaderCheckboxChecked(!isHeaderCheckboxChecked);
    }
  };

  const handleRowCheckboxChange = (reportId: number) => {
    if (
      claims.includes('admin') ||
      claims.includes('reports:expired:delete') ||
      claims.includes('reports:expired:create')
    ) {
      setIsCheckedRows((prevCheckedRows: boolean[]) => ({
        ...prevCheckedRows,
        [reportId]: !prevCheckedRows[reportId],
      }));

      const updatedDeletedReports = formatReports({
        ...isCheckedRows,
        [reportId]: !isCheckedRows[reportId],
      });
      setIsHeaderCheckboxChecked(
        updatedDeletedReports.length === reports.length,
      );
      dispatch(reducerDeletedExpiredReports(updatedDeletedReports));
    }
  };

  const selectChange = (
    event: { value: string; label: string },
    name: string,
  ) => {
    const value = event.value;

    dispatch(
      reducerExpiredReportsInputValues({
        ...expiredReportsInputValues,
        [name]: value === '' ? [] : [value],
      }),
    );

    setIsPending(true);
    dispatch(actionGetExpiredReports(limit, page)).then(() => {
      setIsPending(false);
    });
    resetPages();
  };

  const handleChange = (newLimit: number, newPage: number) => {
    setIsPending(true);
    dispatch(actionGetExpiredReports(newLimit, newPage)).then(() => {
      setIsPending(false);
    });
    localStorage.setItem('pageLimit', JSON.stringify(newLimit));
  };

  const handleChangeLimits = (option: { value: number }) => {
    handleChange(option.value, 1);
    resetPages();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    if (value === '') {
      dispatch(
        reducerExpiredReportsInputValues({
          ...expiredReportsInputValues,
          [name]: null,
        }),
      );
    } else {
      dispatch(
        reducerExpiredReportsInputValues({
          ...expiredReportsInputValues,
          [name]: value,
        }),
      );
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      setIsPending(true);
      dispatch(actionGetExpiredReports(limit, page)).then(() => {
        setIsPending(false);
      });
    }
  };
  return (
    <>
      <div className='loader__wrapper'>
        {(!reports || isPending) && <Loader />}
      </div>
      <div
        className={clsx(
          JSON.parse(IS_VISIBLE_SIDEBAR as string) &&
            isOpenSidebar &&
            'active__table',
          !isOpenSidebar && 'closed__table',
          'table',
        )}
      >
        {status && status !== null && (
          <Toaster
            position='top-center'
            toastOptions={{
              duration: 5000,
            }}
          />
        )}
        <div className='filters__container__mobile'>
          {expiredReportsSettings.includes('date') && (
            <div className='filters__item__mobile'>
              <div>
                <RangeDatePicker
                  setLoading={setIsPending}
                  reducer={reducerExpiredReportsInputValues}
                  inputValues={expiredReportsInputValues}
                  defaultDateFrom={formattedDateFrom}
                  action={actionGetExpiredReports}
                  defaultDateTo={formattedDateTo}
                  setCurrentPage={setCurrentPage}
                  setStartPage={setStartPage}
                />
              </div>
            </div>
          )}
          {expiredReportsSettings.includes('contract') && (
            <div className='filters__item__mobile'>
              <div className='multiselect__filter_block'>
                <CustomMultiSelect
                  setLoading={setIsPending}
                  reducer={reducerExpiredReportsInputValues}
                  inputValues={expiredReportsInputValues}
                  action={actionGetExpiredReports}
                  options={contractsOptions}
                  pageName='expiredReports'
                  isShowPlaceholder={true}
                  resetPages={resetPages}
                  placeholder='Контракт'
                  name='contracts'
                  limit={limit}
                />
              </div>
            </div>
          )}
          {expiredReportsSettings.includes('chain') && (
            <div className='filters__item__mobile'>
              <div className='multiselect__filter_block'>
                <CustomMultiSelect
                  setLoading={setIsPending}
                  reducer={reducerExpiredReportsInputValues}
                  inputValues={expiredReportsInputValues}
                  action={actionGetExpiredReports}
                  pageName='expiredReports'
                  isShowPlaceholder={true}
                  resetPages={resetPages}
                  options={chainsOptions}
                  placeholder='Сеть'
                  limit={limit}
                  name='chains'
                />
              </div>
            </div>
          )}

          {expiredReportsSettings.includes('project') && (
            <div className='filters__item__mobile'>
              <div className='multiselect__filter_block'>
                <CustomMultiSelect
                  setLoading={setIsPending}
                  reducer={reducerExpiredReportsInputValues}
                  inputValues={expiredReportsInputValues}
                  action={actionGetExpiredReports}
                  pageName='expiredReports'
                  options={projectOptions}
                  isShowPlaceholder={true}
                  resetPages={resetPages}
                  placeholder='Проект'
                  name='projects'
                  limit={limit}
                />
              </div>
            </div>
          )}
          {expiredReportsSettings.includes('address') && (
            <div className='filters__item__mobile filters__item__mobile--full'>
              <div className='filter__block'>
                <input
                  defaultValue={
                    expiredReportsInputValues.address &&
                    expiredReportsInputValues.address
                  }
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  autoComplete='off'
                  placeholder=' '
                  name='address'
                  type='text'
                />
                <label>Адрес</label>
              </div>
            </div>
          )}
          {expiredReportsSettings.includes('sv') && showSupervisorsColumn && (
            <div className='filters__item__mobile'>
              <div className='multiselect__filter_block'>
                <CustomMultiSelect
                  setLoading={setIsPending}
                  reducer={reducerExpiredReportsInputValues}
                  inputValues={expiredReportsInputValues}
                  action={actionGetExpiredReports}
                  placeholder='Супервайзер'
                  pageName='expiredReports'
                  isShowPlaceholder={true}
                  resetPages={resetPages}
                  options={svsOptions}
                  limit={limit}
                  name='svs'
                />
              </div>
            </div>
          )}
          {expiredReportsSettings.includes('coordinator') && showSupervisorsColumn && (
            <div className='filters__item__mobile'>
              <div className='multiselect__filter_block'>
                <CustomMultiSelect
                  setLoading={setIsPending}
                  reducer={reducerExpiredReportsInputValues}
                  inputValues={expiredReportsInputValues}
                  action={actionGetExpiredReports}
                  placeholder='Координатор'
                  pageName='expiredReports'
                  isShowPlaceholder={true}
                  resetPages={resetPages}
                  options={vsvsOptions}
                  limit={limit}
                  name='vsv'
                />
              </div>
            </div>
          )}
          {expiredReportsSettings.includes('merch') && (
            <div className='filters__item__mobile'>
              <div className='multiselect__filter_block'>
                <CustomMultiSelect
                  setLoading={setIsPending}
                  reducer={reducerExpiredReportsInputValues}
                  inputValues={expiredReportsInputValues}
                  action={actionGetExpiredReports}
                  placeholder='Мерчендайзер'
                  pageName='expiredReports'
                  isShowPlaceholder={true}
                  resetPages={resetPages}
                  options={merchsOptionsWithEmpty}
                  limit={limit}
                  name='merchs'
                />
              </div>
            </div>
          )}
          {expiredReportsSettings.includes('merchStatus') && (
            <div className='filters__item__mobile'>
              <div className='filter__block'>
                <Select
                  defaultValue={
                    expiredReportsInputValues.statuses &&
                    expiredReportsInputValues.statuses.length &&
                    employeeStatusesOptions.find(
                      (option) =>
                        option.value === expiredReportsInputValues.statuses[0],
                    )
                  }
                  isShowPlaceholder={
                    !!expiredReportsInputValues.statuses?.length ||
                    expiredReportsInputValues.statuses === undefined
                  }
                  propsChange={(event: { value: string; label: string }) =>
                    selectChange(event, 'statuses')
                  }
                  placeholder='Статус мерчендайзера'
                  options={employeeStatusesOptions}
                  className='users__filter'
                  name='statuses'
                />
              </div>
            </div>
          )}
        </div>
        <div className='table__wrapper'>
          <table>
            <thead className='table__header__filters'>
              <tr>
                <th className='reports__checkbox_block'>
                  <input
                    disabled={
                      !(
                        claims.includes('admin') ||
                        claims.includes('reports:expired:delete') ||
                        claims.includes('reports:expired:create')
                      )
                    }
                    onChange={handleHeaderCheckboxChange}
                    checked={isHeaderCheckboxChecked}
                    type='checkbox'
                  />
                </th>
                {expiredReportsSettings.includes('date') && (
                  <th>
                    <div>
                      <RangeDatePicker
                        setLoading={setIsPending}
                        reducer={reducerExpiredReportsInputValues}
                        inputValues={expiredReportsInputValues}
                        defaultDateFrom={formattedDateFrom}
                        action={actionGetExpiredReports}
                        defaultDateTo={formattedDateTo}
                        setCurrentPage={setCurrentPage}
                        setStartPage={setStartPage}
                      />
                    </div>
                  </th>
                )}
                {expiredReportsSettings.includes('contract') && (
                  <th>
                    <div className='multiselect__filter_block'>
                      <CustomMultiSelect
                        setLoading={setIsPending}
                        reducer={reducerExpiredReportsInputValues}
                        inputValues={expiredReportsInputValues}
                        action={actionGetExpiredReports}
                        options={contractsOptions}
                        pageName='expiredReports'
                        isShowPlaceholder={true}
                        resetPages={resetPages}
                        placeholder='Контракт'
                        name='contracts'
                        limit={limit}
                      />
                    </div>
                  </th>
                )}
                {expiredReportsSettings.includes('chain') && (
                  <th>
                    <div className='multiselect__filter_block'>
                      <CustomMultiSelect
                        setLoading={setIsPending}
                        reducer={reducerExpiredReportsInputValues}
                        inputValues={expiredReportsInputValues}
                        action={actionGetExpiredReports}
                        pageName='expiredReports'
                        isShowPlaceholder={true}
                        resetPages={resetPages}
                        options={chainsOptions}
                        placeholder='Сеть'
                        limit={limit}
                        name='chains'
                      />
                    </div>
                  </th>
                )}
                {expiredReportsSettings.includes('address') && (
                  <th>
                    <div className='filter__block'>
                      <input
                        defaultValue={
                          expiredReportsInputValues.address &&
                          expiredReportsInputValues.address
                        }
                        onChange={handleInputChange}
                        onKeyPress={handleKeyPress}
                        autoComplete='off'
                        placeholder=' '
                        name='address'
                        type='text'
                      />
                      <label>Адрес</label>
                    </div>
                  </th>
                )}
                {expiredReportsSettings.includes('project') && (
                  <th>
                    <div className='multiselect__filter_block'>
                      <CustomMultiSelect
                        setLoading={setIsPending}
                        reducer={reducerExpiredReportsInputValues}
                        inputValues={expiredReportsInputValues}
                        action={actionGetExpiredReports}
                        pageName='expiredReports'
                        options={projectOptions}
                        isShowPlaceholder={true}
                        resetPages={resetPages}
                        placeholder='Проект'
                        name='projects'
                        limit={limit}
                      />
                    </div>
                  </th>
                )}
                {expiredReportsSettings.includes('sv') && showSupervisorsColumn && (
                  <th>
                    <div className='multiselect__filter_block'>
                      <CustomMultiSelect
                        setLoading={setIsPending}
                        reducer={reducerExpiredReportsInputValues}
                        inputValues={expiredReportsInputValues}
                        action={actionGetExpiredReports}
                        placeholder='Супервайзер'
                        pageName='expiredReports'
                        isShowPlaceholder={true}
                        resetPages={resetPages}
                        options={svsOptions}
                        limit={limit}
                        name='svs'
                      />
                    </div>
                  </th>
                )}
                {expiredReportsSettings.includes('coordinator') && showSupervisorsColumn && (
                  <th>
                    <div className='multiselect__filter_block'>
                      <CustomMultiSelect
                        setLoading={setIsPending}
                        reducer={reducerExpiredReportsInputValues}
                        inputValues={expiredReportsInputValues}
                        action={actionGetExpiredReports}
                        placeholder='Координатор'
                        pageName='expiredReports'
                        isShowPlaceholder={true}
                        resetPages={resetPages}
                        options={vsvsOptions}
                        limit={limit}
                        name='vsv'
                      />
                    </div>
                  </th>
                )}
                {expiredReportsSettings.includes('merch') && (
                  <th>
                    <div className='multiselect__filter_block'>
                      <CustomMultiSelect
                        setLoading={setIsPending}
                        reducer={reducerExpiredReportsInputValues}
                        inputValues={expiredReportsInputValues}
                        action={actionGetExpiredReports}
                        placeholder='Мерчендайзер'
                        pageName='expiredReports'
                        isShowPlaceholder={true}
                        resetPages={resetPages}
                        options={merchsOptionsWithEmpty}
                        limit={limit}
                        name='merchs'
                      />
                    </div>
                  </th>
                )}
                {expiredReportsSettings.includes('merchStatus') && (
                  <th>
                    <div className='filter__block'>
                      <Select
                        defaultValue={
                          expiredReportsInputValues.statuses &&
                          expiredReportsInputValues.statuses.length &&
                          employeeStatusesOptions.find(
                            (option) =>
                              option.value ===
                              expiredReportsInputValues.statuses[0],
                          )
                        }
                        isShowPlaceholder={
                          !!expiredReportsInputValues.statuses?.length ||
                          expiredReportsInputValues.statuses === undefined
                        }
                        propsChange={(event: {
                          value: string;
                          label: string;
                        }) => selectChange(event, 'statuses')}
                        placeholder='Статус мерчендайзера'
                        options={employeeStatusesOptions}
                        className='users__filter'
                        name='statuses'
                      />
                    </div>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {reports &&
                reports?.map((report: IExpiredReportOptions, index: number) => {
                  return (
                    <tr
                      key={index}
                      className={clsx(
                        'active__link_tr',
                        getExpiredReportRowClassName(report),
                      )}
                    >
                      <td>
                        <input
                          disabled={
                            !(
                              claims.includes('admin') ||
                              claims.includes('reports:expired:delete') ||
                              claims.includes('reports:expired:create')
                            )
                          }
                          onChange={() =>
                            handleRowCheckboxChange(report.expiredReport.id)
                          }
                          checked={
                            (isCheckedRows &&
                              isCheckedRows[report.expiredReport.id]) ||
                            false
                          }
                          type='checkbox'
                        />
                      </td>
                      {expiredReportsSettings.includes('date') && (
                        <td>{report.date.split('-').reverse().join('.')}</td>
                      )}
                      {expiredReportsSettings.includes('contract') && (
                        <td>{report.contract}</td>
                      )}
                      {expiredReportsSettings.includes('chain') && (
                        <td>{report.pos.chain}</td>
                      )}
                      {expiredReportsSettings.includes('address') && (
                        <td>
                          {'г.' + report.pos.city + ', ' + report.pos.address}
                        </td>
                      )}
                      {expiredReportsSettings.includes('project') && (
                        <td>{report.project}</td>
                      )}
                      {expiredReportsSettings.includes('sv') && showSupervisorsColumn && (
                        <td>
                          {shouldShowSupervisorsInReports(report) ? (
                            <>
                              {report && report?.sv?.familyName}{' '}
                              {report && report?.sv?.givenName}{' '}
                              {report && report?.sv?.middleName}
                            </>
                          ) : null}
                        </td>
                      )}
                      {expiredReportsSettings.includes('coordinator') && showSupervisorsColumn && (
                        <td>
                          {shouldShowSupervisorsInReports(report) ? (
                            <>
                              {report && report?.vsv?.familyName}{' '}
                              {report && report?.vsv?.givenName}{' '}
                              {report && report?.vsv?.middleName}
                            </>
                          ) : null}
                        </td>
                      )}
                      {expiredReportsSettings.includes('merch') && (
                        <td>{report && report?.merch?.login}</td>
                      )}
                      {expiredReportsSettings.includes('merchStatus') && (
                        <td>
                          {report.merch &&
                            report.merch.status &&
                            Object.values(report.merch.status)}
                        </td>
                      )}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        <div className='pagination'>
          <div className='pagination__count'>Всего: {expiredReportsCount}</div>
          <div className='pagination__str'>
            <span>Отображать строки</span>
            <LimitSelect propsChange={handleChangeLimits} limit={actualLimit} />
          </div>
          <Pagination
            setCurrentPage={setCurrentPage}
            setStartPage={setStartPage}
            handleChange={handleChange}
            count={expiredReportsCount}
            currentPage={currentPage}
            pageName='userReports'
            startPage={startPage}
            limit={actualLimit}
          />
        </div>
      </div>
    </>
  );
};

export default TableExpiredReports;
